import {
  fetchClientByClientIdRequest,
  putMirroredFundingSourceForClientByClientIdRequest,
} from '@/model';
import { selectClient } from '@client/model/selectors';
import { useDispatch, useSelector } from 'react-redux';
import { useQueryClient } from '@tanstack/react-query';
import { clientsApi, clientKeys } from '@client/api/waterlily';
import {
  findPartnerLinkedSource,
  isPartnerSharedRecipient,
  splitFundingSourceFields,
} from '@/components/funding-sources-coordination/partner-funding-utils';
import type { AllocationOption } from '@/components/funding-sources-coordination/partner-allocation-section';

export interface PartnerSaveConfig {
  fundingSourceId: string | undefined;
  source: SelfFundingSource;
  isExistingMirror: boolean;
  allocationOption: AllocationOption;
  clientPercent: number;
}

export function useVehiclePartnerActions() {
  const dispatch = useDispatch();
  const queryClient = useQueryClient();
  const client = useSelector(selectClient);

  const partnerClientId = client?.partnerClientId ?? null;
  const partnerFirstName = client?.mutableClientPartner?.clientFirstName;
  const partnerSelfFunding = client?.mutableClientPartner?.multipleFundingSources?.selfFunding;

  function dispatchMirrored(
    clientOperation: PutMirroredFundingSourceBodyProps['clientOperation'],
    partnerOperation: PutMirroredFundingSourceBodyProps['partnerOperation'],
  ) {
    if (!partnerClientId || !client?.clientId) return;
    dispatch(
      putMirroredFundingSourceForClientByClientIdRequest({
        clientId: client.clientId,
        clientOperation,
        partnerClientId,
        partnerOperation,
      }),
    );
  }

  function handleMirrorToPartner(sourceId: string, source: SelfFundingSource) {
    if (!client?.clientId) return;
    const groupId = source.fundingSourceGroupId ?? crypto.randomUUID();
    // Stamp the originator on both copies so the partner is correctly detected
    // as the recipient (and locked to read-only) on their side.
    const mirroredSource = {
      ...source,
      partnerAction: 'mirrored' as const,
      fundingSourceGroupId: groupId,
      addedByClientId: source.addedByClientId ?? client.clientId,
    };

    dispatchMirrored(
      { fundingSourceId: sourceId, fundingSourceData: mirroredSource },
      { fundingSourceId: null, fundingSourceData: mirroredSource },
    );
  }

  function handleSyncToPartner(
    sourceId: string,
    source: SelfFundingSource,
    partnerOverrides?: Partial<SelfFundingSource>,
  ) {
    if (!source.fundingSourceGroupId) return;

    const partnerSourceEntry = findPartnerLinkedSource(
      partnerSelfFunding,
      source.fundingSourceGroupId,
    );
    if (!partnerSourceEntry) return;

    const [partnerFundingSourceId, partnerSource] = partnerSourceEntry;
    const isSplit = source.splitRatio != null;

    // Preserve the existing originator on re-saves; only stamp if missing.
    const addedByClientId =
      source.addedByClientId ?? partnerSource.addedByClientId ?? client?.clientId ?? null;

    dispatchMirrored(
      {
        fundingSourceId: sourceId,
        fundingSourceData: { ...source, addedByClientId },
      },
      {
        fundingSourceId: partnerFundingSourceId,
        fundingSourceData: {
          ...source,
          partnerAction: 'mirrored',
          fundingSourceGroupId: source.fundingSourceGroupId,
          addedByClientId,
          ...(isSplit && {
            existingAssetsValue: partnerSource.existingAssetsValue,
            monthlyContributionAmount: partnerSource.monthlyContributionAmount,
            monthlyIncome: partnerSource.monthlyIncome,
            drawdownAmount: partnerSource.drawdownAmount,
            splitRatio: partnerSource.splitRatio,
          }),
          ...partnerOverrides,
        },
      },
    );
  }

  async function handleUnlink(
    sourceId: string,
    source: SelfFundingSource,
    ownerRestoreOverride?: Partial<SelfFundingSource>,
  ) {
    if (!source.fundingSourceGroupId) return;

    // Recipient status comes from `addedByClientId` and is independent of
    // whether the partner copy is currently in local cache.
    const currentIsRecipient = isPartnerSharedRecipient(source, client?.clientId);

    let partnerSourceEntry = findPartnerLinkedSource(
      partnerSelfFunding,
      source.fundingSourceGroupId,
    );

    // If the partner copy isn't in local state (stale cache / race), refetch
    // the partner client once and retry the lookup. Without this, recipients
    // would fall into the owner branch (leaving the real owner's copy
    // orphaned) and owner-side splits would skip the auto-sum (losing the
    // partner's portion in the restored total).
    if (!partnerSourceEntry && partnerClientId) {
      try {
        const response = (await clientsApi.getById(
          partnerClientId,
        )) as unknown as ClientContainer;
        const freshPartnerFunding = response.client?.multipleFundingSources?.selfFunding;
        partnerSourceEntry = findPartnerLinkedSource(
          freshPartnerFunding,
          source.fundingSourceGroupId,
        );
        if (client?.clientId) {
          dispatch(fetchClientByClientIdRequest(client.clientId));
        }
        queryClient.invalidateQueries({ queryKey: clientKeys.detail(partnerClientId) });
      } catch (err) {
        console.error('handleUnlink: failed to refetch partner data', err);
      }
    }

    // Unlink fully removes one copy. The combined total goes to the originator
    // (whoever owns the source via `addedByClientId`); the other copy is
    // deleted. This way the result is identical regardless of who triggers the
    // unlink — owner ends up with the full amount, recipient is removed.
    const wasSplit = source.splitRatio != null && partnerSourceEntry;
    const partnerSource = partnerSourceEntry?.[1];

    const sumIfPresent = (
      a: number | null | undefined,
      b: number | null | undefined,
    ): number | undefined => {
      if (a == null && b == null) return undefined;
      return (a ?? 0) + (b ?? 0);
    };

    // When the caller already holds the combined totals (e.g. the modal seeds
    // its form with them and the user may have edited them before unlinking),
    // use the explicit override instead of summing — re-summing would either
    // discard the user's edits or double-count them.
    const ownerRestore: Partial<SelfFundingSource> = ownerRestoreOverride ?? {};
    if (!ownerRestoreOverride && wasSplit && partnerSource) {
      const eav = sumIfPresent(source.existingAssetsValue, partnerSource.existingAssetsValue);
      if (eav !== undefined) ownerRestore.existingAssetsValue = eav;
      const mca = sumIfPresent(
        source.monthlyContributionAmount,
        partnerSource.monthlyContributionAmount,
      );
      if (mca !== undefined) ownerRestore.monthlyContributionAmount = mca;
      const mi = sumIfPresent(source.monthlyIncome, partnerSource.monthlyIncome);
      if (mi !== undefined) ownerRestore.monthlyIncome = mi;
      const dd = sumIfPresent(source.drawdownAmount, partnerSource.drawdownAmount);
      if (dd !== undefined) ownerRestore.drawdownAmount = dd;
    }

    if (currentIsRecipient) {
      if (partnerSource && partnerSourceEntry) {
        // Current client is the recipient — delete this side, restore owner
        // (partner) to combined total, clear link metadata.
        dispatchMirrored(
          { fundingSourceId: sourceId, fundingSourceData: null },
          {
            fundingSourceId: partnerSourceEntry[0],
            fundingSourceData: {
              ...partnerSource,
              ...ownerRestore,
              partnerAction: null,
              fundingSourceGroupId: null,
              splitRatio: null,
              addedByClientId: null,
            },
          },
        );
      } else {
        // Refetch failed and partner copy is unreachable. Best-effort: delete
        // our (recipient) side. The owner's copy will be left orphaned with
        // dangling mirror metadata until they next touch it.
        console.warn(
          `handleUnlink: recipient unlink without reachable partner source for groupId=${source.fundingSourceGroupId}; owner's copy may be left orphaned.`,
        );
        dispatchMirrored(
          { fundingSourceId: sourceId, fundingSourceData: null },
          { fundingSourceId: null, fundingSourceData: null },
        );
      }
      return;
    }

    // Current client is the owner (or legacy with no addedByClientId) — restore
    // current side and delete the partner copy.
    const partnerOp: PutMirroredFundingSourceBodyProps['partnerOperation'] = partnerSourceEntry
      ? { fundingSourceId: partnerSourceEntry[0], fundingSourceData: null }
      : { fundingSourceId: null, fundingSourceData: null };

    dispatchMirrored(
      {
        fundingSourceId: sourceId,
        fundingSourceData: {
          ...source,
          ...ownerRestore,
          partnerAction: null,
          fundingSourceGroupId: null,
          splitRatio: null,
          addedByClientId: null,
        },
      },
      partnerOp,
    );
  }

  function handleDeleteMirrored(
    fundingSourceId: string,
    fundingSourceGroupId: string,
    mode: 'both' | 'client-only',
  ) {
    const partnerEntry = findPartnerLinkedSource(partnerSelfFunding, fundingSourceGroupId);

    if (mode === 'both') {
      dispatchMirrored(
        { fundingSourceId, fundingSourceData: null },
        partnerEntry
          ? { fundingSourceId: partnerEntry[0], fundingSourceData: null }
          : { fundingSourceId: null, fundingSourceData: null },
      );
    } else {
      const partnerOp: PutMirroredFundingSourceBodyProps['partnerOperation'] = partnerEntry
        ? {
            fundingSourceId: partnerEntry[0],
            fundingSourceData: {
              ...partnerEntry[1],
              partnerAction: null,
              fundingSourceGroupId: null,
            },
          }
        : { fundingSourceId: null, fundingSourceData: null };

      dispatchMirrored(
        { fundingSourceId, fundingSourceData: null },
        partnerOp,
      );
    }
  }

  function saveWithPartnerSync({
    fundingSourceId,
    source,
    isExistingMirror,
    allocationOption,
    clientPercent,
  }: PartnerSaveConfig) {
    if (!partnerClientId || !client?.clientId) return;

    const isMirrorOption = allocationOption === 'mirror';
    const effectivePercent = allocationOption === 'split-50-50' ? 50 : clientPercent;

    // Reuse the existing partner source ID and group ID when re-saving an existing
    // mirror — never orphan the link by minting a new group.
    const existingGroupId = isExistingMirror ? source.fundingSourceGroupId ?? null : null;
    const existingPartnerEntry = existingGroupId
      ? findPartnerLinkedSource(partnerSelfFunding, existingGroupId)
      : undefined;
    const existingPartnerSourceId = existingPartnerEntry?.[0] ?? null;
    const groupId = existingGroupId ?? crypto.randomUUID();

    // Stamp the originator on both sides. Preserve the existing originator on
    // re-saves so changing a ratio doesn't transfer ownership.
    const addedByClientId = source.addedByClientId ?? client.clientId;

    const baseSource = {
      ...source,
      partnerAction: 'mirrored' as const,
      fundingSourceGroupId: groupId,
      addedByClientId,
    };

    if (isMirrorOption) {
      // Both partners get the full amount.
      const mirroredSource: SelfFundingSource = {
        ...baseSource,
        splitRatio: null,
      };
      dispatchMirrored(
        { fundingSourceId: fundingSourceId ?? null, fundingSourceData: mirroredSource },
        { fundingSourceId: existingPartnerSourceId, fundingSourceData: mirroredSource },
      );
      return;
    }

    // Split: source has the combined totals (modal reconstructs them on open),
    // so split it by the chosen ratio and write each portion to the matching side.
    const { clientOverrides, partnerOverrides } = splitFundingSourceFields(source, effectivePercent);

    dispatchMirrored(
      {
        fundingSourceId: fundingSourceId ?? null,
        fundingSourceData: {
          ...baseSource,
          ...clientOverrides,
          splitRatio: effectivePercent,
        },
      },
      {
        fundingSourceId: existingPartnerSourceId,
        fundingSourceData: {
          ...baseSource,
          ...partnerOverrides,
          splitRatio: 100 - effectivePercent,
        },
      },
    );
  }

  return {
    partnerFirstName,
    partnerClientId,
    handleMirrorToPartner,
    handleSyncToPartner,
    handleUnlink,
    handleDeleteMirrored,
    saveWithPartnerSync,
  };
}
